import { CampaignStatus } from "@gen/tracker/v1/campaign_pb";
import {
  DimensionFilter,
  DimensionFilterOp,
  MetricFilter,
  MetricFilterOp,
  ReportViewDefinition,
} from "@gen/tracker/v1/reporting_pb";

/**
 * What the campaign list shows, as its address (boards CampList, CampGroups,
 * CampFilter, CampSaveView, CampColumns). Pure: the URL ↔ state ↔ request ↔
 * saved view mappings are what the tests pin.
 *
 * The period, the comparison and «Мои/Все» are the Top bar's and are not
 * here; a saved view of the list never keeps a period (board CampSaveView).
 */

/** «Списком · По группам · По офферам · По баерам» → ListCampaignsRequest.group_by. */
export type Fold = "list" | "groups" | "offers" | "buyers";
export const FOLDS: readonly Fold[] = ["list", "groups", "offers", "buyers"];
export const FOLD_GROUP_BY: Record<Fold, string> = { list: "", groups: "group", offers: "offer", buyers: "owner" };

/**
 * The quick views (board CampList): «Активные» is every campaign outside
 * the archive, «Архив» the archive, and the three in between are the
 * server's flags (ListCampaignsRequest.quick).
 */
export type Quick = "all" | "losing" | "noclicks" | "hold50" | "archive";
export const QUICKS: readonly Quick[] = ["all", "losing", "noclicks", "hold50", "archive"];
export const QUICK_SERVER: Partial<Record<Quick, string>> = {
  losing: "losing",
  noclicks: "no_clicks",
  hold50: "hold_over_half",
};

/** The dimension filters (FacetFilter) and the report dimension each is saved as. */
export type FacetId = "owner" | "source" | "group" | "geo" | "offer" | "status";
export const FACET_IDS: readonly FacetId[] = ["owner", "source", "group", "geo", "offer", "status"];
const FACET_KEY: Record<FacetId, string> = { owner: "own", source: "src", group: "grp", geo: "geo", offer: "ofr", status: "st" };
const FACET_DIMENSION: Partial<Record<FacetId, string>> = {
  owner: "owner",
  source: "source",
  group: "group",
  geo: "country",
  offer: "offer",
};
/** The campaign statuses the «Статус» facet offers; the archive is its own quick view. */
export const STATUS_OPTIONS = ["ACTIVE", "PAUSED", "DRAFT"] as const;
export type StatusOption = (typeof STATUS_OPTIONS)[number];

export type ConditionOp = "lt" | "lte" | "gt" | "gte" | "eq" | "neq";
const OPS: readonly ConditionOp[] = ["lt", "lte", "gt", "gte", "eq", "neq"];
const OP_PROTO: Record<ConditionOp, MetricFilterOp> = {
  lt: MetricFilterOp.LT,
  lte: MetricFilterOp.LTE,
  gt: MetricFilterOp.GT,
  gte: MetricFilterOp.GTE,
  eq: MetricFilterOp.EQ,
  neq: MetricFilterOp.NEQ,
};

/** One condition on the period's numbers: «ROI < 0 %». The value is as typed — a percent as a percent. */
export interface MetricCondition {
  metric: string;
  op: ConditionOp;
  value: string;
}

export type Facets = Partial<Record<FacetId, string[]>>;

export interface ListState {
  fold: Fold;
  quick: Quick;
  facets: Facets;
  conditions: MetricCondition[];
  search: string;
}

export function parseFold(raw: string | null | undefined): Fold {
  return (FOLDS as readonly string[]).includes(raw ?? "") ? (raw as Fold) : "list";
}

export function parseQuick(raw: string | null | undefined): Quick {
  return (QUICKS as readonly string[]).includes(raw ?? "") && raw !== "all" ? (raw as Quick) : "all";
}

/** `roi:lt:0;cost:gt:150` ↔ conditions. Junk parts are dropped, never thrown. Pure. */
export function parseConditions(raw: string | null | undefined): MetricCondition[] {
  if (!raw) return [];
  return raw.split(";").flatMap((part) => {
    const [metric, op, ...rest] = part.split(":");
    const value = rest.join(":").trim();
    if (!metric || !(OPS as readonly string[]).includes(op ?? "") || value === "") return [];
    return [{ metric, op: op as ConditionOp, value }];
  });
}

export function formatConditions(cs: readonly MetricCondition[]): string {
  return cs
    .filter((c) => c.metric && c.value.trim() !== "")
    .map((c) => `${c.metric}:${c.op}:${c.value.trim()}`)
    .join(";");
}

const list = (raw: string | null) => (raw ? raw.split(",").map((s) => s.trim()).filter(Boolean) : []);

/** The list's own keys out of the address. Pure. */
export function readState(get: (key: string) => string | null): ListState {
  const facets: Facets = {};
  for (const id of FACET_IDS) {
    const values = list(get(FACET_KEY[id]));
    if (values.length > 0) facets[id] = id === "geo" ? values.map((v) => v.toUpperCase()) : values;
  }
  return {
    fold: parseFold(get("g")),
    quick: parseQuick(get("qv")),
    facets,
    conditions: parseConditions(get("f")),
    search: get("q") ?? "",
  };
}

/** The URL patch for a state (null deletes a key). Pure. */
export function statePatch(s: Partial<ListState>): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  if (s.fold !== undefined) out.g = s.fold === "list" ? null : s.fold;
  if (s.quick !== undefined) out.qv = s.quick === "all" ? null : s.quick;
  if (s.facets !== undefined) {
    for (const id of FACET_IDS) {
      const v = s.facets[id];
      out[FACET_KEY[id]] = v && v.length > 0 ? v.join(",") : null;
    }
  }
  if (s.conditions !== undefined) out.f = formatConditions(s.conditions) || null;
  if (s.search !== undefined) out.q = s.search || null;
  return out;
}

/** Every key the list writes, for «Активные» to clear. */
export const LIST_KEYS = ["g", "qv", "f", "q", ...FACET_IDS.map((id) => FACET_KEY[id])];

/**
 * A percent metric is typed as a percent («ROI < 0 %», «CR > 4,5») and
 * compared by the server as a ratio. Pure.
 */
export function conditionValueForServer(value: string, percent: boolean): string | undefined {
  const n = Number.parseFloat(value.replace(",", ".").replace(/\s|%/g, ""));
  if (!Number.isFinite(n)) return undefined;
  return String(percent ? n / 100 : n);
}

export function conditionValueFromServer(value: string, percent: boolean): string {
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n)) return value;
  return String(percent ? Math.round(n * 1e6) / 1e4 : n);
}

export function metricFilters(cs: readonly MetricCondition[], isPercent: (metric: string) => boolean): MetricFilter[] {
  return cs.flatMap((c) => {
    const value = conditionValueForServer(c.value, isPercent(c.metric));
    return value === undefined ? [] : [new MetricFilter({ metricKey: c.metric, op: OP_PROTO[c.op], value })];
  });
}

export function statusesOf(state: Pick<ListState, "quick" | "facets">): CampaignStatus[] {
  if (state.quick === "archive") return [CampaignStatus.ARCHIVED];
  const picked = (state.facets.status ?? []).flatMap((s) => {
    const v = CampaignStatus[s as keyof typeof CampaignStatus];
    return typeof v === "number" ? [v] : [];
  });
  return picked.length > 0 ? picked : [CampaignStatus.ACTIVE, CampaignStatus.PAUSED, CampaignStatus.DRAFT];
}

/**
 * The state as a saved view of the list (ReportViewService, surface
 * «campaigns»). No period and no basis: the list's period is the Top bar's.
 * A flag quick view («Сливают» …) is kept by its server name, so the view
 * reopens on the set the server counts; the archive is a status. Pure.
 */
export function toDefinition(
  state: ListState,
  columns: readonly string[],
  sort: { key: string; desc: boolean },
  isPercent: (metric: string) => boolean,
): ReportViewDefinition {
  const dimensionFilters: DimensionFilter[] = [];
  for (const id of FACET_IDS) {
    const dim = FACET_DIMENSION[id];
    const values = state.facets[id];
    if (dim && values && values.length > 0) {
      dimensionFilters.push(new DimensionFilter({ dimensionKey: dim, op: DimensionFilterOp.IN, values }));
    }
  }
  const statuses =
    state.quick === "archive" ? ["ARCHIVED"] : (state.facets.status ?? []).filter((s) => (STATUS_OPTIONS as readonly string[]).includes(s));
  return new ReportViewDefinition({
    surface: "campaigns",
    metricKeys: [...columns],
    sortMetricKey: sort.key,
    sortDesc: sort.desc,
    grouping: FOLD_GROUP_BY[state.fold],
    dimensionFilters,
    metricFilters: metricFilters(state.conditions, isPercent),
    statuses,
    quick: QUICK_SERVER[state.quick] ?? "",
  });
}

const OP_FROM_PROTO = new Map<MetricFilterOp, ConditionOp>(
  (Object.entries(OP_PROTO) as [ConditionOp, MetricFilterOp][]).map(([k, v]) => [v, k]),
);
const QUICK_FROM_SERVER = new Map<string, Quick>((Object.entries(QUICK_SERVER) as [Quick, string][]).map(([k, v]) => [v, k]));
const DIMENSION_FACET = new Map<string, FacetId>(
  (Object.entries(FACET_DIMENSION) as [FacetId, string][]).map(([k, v]) => [v, k]),
);

/** A saved view back into the address: the list's keys, the columns and the sort. Pure. */
export function fromDefinition(
  d: ReportViewDefinition,
  isPercent: (metric: string) => boolean,
): { state: ListState; columns: string[]; sort: { key: string; desc: boolean } | undefined } {
  const facets: Facets = {};
  for (const f of d.dimensionFilters) {
    const id = DIMENSION_FACET.get(f.dimensionKey);
    if (id && f.op === DimensionFilterOp.IN && f.values.length > 0) facets[id] = [...f.values];
  }
  const archived = d.statuses.includes("ARCHIVED");
  const statuses = d.statuses.filter((s) => (STATUS_OPTIONS as readonly string[]).includes(s));
  if (!archived && statuses.length > 0) facets.status = statuses;
  const fold = (Object.entries(FOLD_GROUP_BY) as [Fold, string][]).find(([, g]) => g === d.grouping)?.[0] ?? "list";
  return {
    state: {
      fold,
      quick: archived ? "archive" : (QUICK_FROM_SERVER.get(d.quick) ?? "all"),
      facets,
      conditions: d.metricFilters.flatMap((f) => {
        const op = OP_FROM_PROTO.get(f.op);
        return op ? [{ metric: f.metricKey, op, value: conditionValueFromServer(f.value, isPercent(f.metricKey)) }] : [];
      }),
      search: "",
    },
    columns: [...d.metricKeys],
    sort: d.sortMetricKey ? { key: d.sortMetricKey, desc: d.sortDesc } : undefined,
  };
}
